// ========================================
// ADD-ON UPSELL MODULE
// Supports: Standard products (via the theme's own cartUpdate pub/sub
// event) and Customily-personalized products (via Customily's own
// window.customilyShopify.hooks.cart.onItemAdded hook — confirmed directly
// with Customily's dev, see /Users/kthoong/.claude/plans/generic-whistling-neumann.md).
// Collects selections from both addon UIs: the checkbox-based "gift box"
// upsell (snippets/add-on-product.liquid) and the catalog-style variant
// picker (snippets/product-addon-catalog.liquid, assets/product-addon-catalog.js).
//
// Exactly one of the two trigger mechanisms below ever runs for a given
// product: state.customilyHookRegistered gates the cartUpdate subscriber
// off once the Customily hook is registered, so a personalized product's
// main-product add-to-cart (however Customily itself triggers it) is never
// also picked up by the standard-flow listener for the same submission.
// ========================================

/**
 * Configuration
 */
const CONFIG = {
  selectors: {
    wrapper: ".gift-box-wrapper, .catalog-product-addon",
    checkboxes: ".gift-box .upsell-checkbox",
    catalogItems: '.catalog-grid-card__item[data-variant-id]:not([data-label="no-thanks"])',
    // Per-product marker Customily injects into the DOM only for products it
    // actually manages personalization/ATC for (inherited from the pre-refactor
    // baseline's isCustomilyActive() check — re-confirm against a live
    // Customily-enabled product page if Customily ever changes this markup).
    customilyApp: ".customily-main-app1",
  },
  endpoints: {
    cartAdd: "/cart/add.js",
  },
};

/**
 * State management
 */
const state = {
  selectedAddons: new Set(),
  isProcessing: false,
  // True once the Customily onItemAdded hook (setupCustomilyHook) is
  // registered, so the cartUpdate subscription (setupCartUpdateHandler)
  // knows to stay out of the way instead of also adding addons for the
  // same submission.
  customilyHookRegistered: false,
};

/**
 * Initialize module on DOM ready
 */
document.addEventListener("DOMContentLoaded", () => {
  const wrapper = document.querySelector(CONFIG.selectors.wrapper);
  if (!wrapper) return;

  initializeAddonSelection();
  setupCartUpdateHandler();
  setupCustomilyHook();
  setupAddonModalSliders();
});

/**
 * Wire up prev/next navigation for the image gallery inside each
 * "view details" modal (assets/add-on-product.js's own modal, not the
 * catalog-picker one in product-addon-catalog.js). Modal-dialog relocates
 * itself to be a direct child of <body> on connect (assets/global.js), so
 * this queries from `document`, not any page-scoped wrapper.
 */
const setupAddonModalSliders = () => {
  document.querySelectorAll(".addon-modal__image").forEach((gallery) => {
    const slides = Array.from(gallery.querySelectorAll(".addon-modal__image-slide"));
    if (slides.length < 2) return;

    const showSlide = (index) => {
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle("is-visible", slideIndex === index);
      });
    };

    gallery.querySelectorAll(".slider-button").forEach((button) => {
      button.addEventListener("click", () => {
        const currentIndex = slides.findIndex((slide) => slide.classList.contains("is-visible"));
        const direction = parseInt(button.dataset.direction, 10) || 1;
        showSlide(getCyclicIndex(currentIndex, direction, slides.length));
      });
    });
  });
};

/**
 * Initialize addon checkbox selection tracking
 */
const initializeAddonSelection = () => {
  const checkboxes = document.querySelectorAll(CONFIG.selectors.checkboxes);

  checkboxes.forEach((checkbox) => {
    const variantId = parseInt(checkbox.dataset.variantId);
    if (isNaN(variantId)) return;

    // Track initially checked addons
    if (checkbox.checked) {
      state.selectedAddons.add(variantId);
    }

    // Listen for changes
    checkbox.addEventListener("change", () => {
      if (checkbox.checked) {
        state.selectedAddons.add(variantId);
      } else {
        state.selectedAddons.delete(variantId);
      }
    });
  });
};

/**
 * Standard (non-Customily) product flow, via the theme's pub/sub bus
 * (assets/pubsub.js) instead of the form's submit event.
 *
 * assets/product-form.js publishes PUB_SUB_EVENTS.cartUpdate only AFTER its
 * own /cart/add request for the main product has already succeeded. Adding
 * addons from that subscriber means our request only ever starts once the
 * main product is confirmed in the cart — never at the same time as it —
 * which avoids the concurrent-request corruption that hijacking the form's
 * own submit event caused in earlier versions of this file (main product
 * added twice, or the addon request getting cancelled).
 */
const setupCartUpdateHandler = () => {
  subscribe(PUB_SUB_EVENTS.cartUpdate, async (data) => {
    if (data?.source !== "product-form") return;
    if (state.customilyHookRegistered) return; // Customily's own hook handles this product
    if (!hasAddonSelections() || state.isProcessing) return;

    state.isProcessing = true;

    try {
      await addAddonsToCart();
    } catch (error) {
      console.error("Failed to add addons:", error);
    } finally {
      state.isProcessing = false;
    }
  });
};

/**
 * Register the Customily integration hook that lets us tag along on
 * Customily's own add-to-cart flow, for personalized products.
 *
 * Structure confirmed directly with Customily's own dev: wrap in a
 * `new Promise` and always `resolve(true)`, even on error, so a failed
 * addon add never blocks Customily's own add-to-cart flow.
 *
 * Called once at page init, NOT from the form's submit event — Customily
 * shows its own personalization/upsell modal in front of the Add to cart
 * button, so the native form submit may never fire directly for these
 * products. Neither window.customilyShopify nor the CONFIG.selectors.customilyApp
 * marker may exist yet at DOMContentLoaded if Customily's own script is still
 * loading, so this retries briefly instead of giving up after one check.
 *
 * Gated on CONFIG.selectors.customilyApp (not just window.customilyShopify
 * existing) because Customily's SDK script loads globally on every product
 * page, not only personalized ones — without this per-product check,
 * state.customilyHookRegistered would end up true on every page once the SDK
 * loads, permanently disabling the standard cartUpdate flow below even for
 * ordinary, non-Customily products.
 */
const setupCustomilyHook = () => {
  const registerHook = () => {
    if (typeof window.customilyShopify === "undefined") return false;
    if (!document.querySelector(CONFIG.selectors.customilyApp)) return false;

    ensureCustomilyHookStructure();

    window.customilyShopify.hooks.cart.onItemAdded = async function () {
      return new Promise(async function (resolve) {
        try {
          if (hasAddonSelections()) await addAddonsToCart();
          resolve(true); // Continue Customily flow
        } catch (error) {
          console.error("Failed to add addons:", error);
          resolve(true); // Still continue even if add-ons fail
        }
      });
    };

    state.customilyHookRegistered = true;
    return true;
  };

  if (registerHook()) return;

  const retryInterval = setInterval(() => {
    if (registerHook()) clearInterval(retryInterval);
  }, 200);

  // If Customily's SDK/marker still hasn't shown up by the deadline, give up
  // registering the hook for this page load rather than polling forever.
  // Logged because a shopper who times out here on a genuinely Customily
  // product would fall through to the standard cartUpdate flow instead —
  // worth being able to spot in the console rather than failing silently.
  setTimeout(() => {
    clearInterval(retryInterval);
    if (!state.customilyHookRegistered) {
      console.warn("Customily hook not registered within 20s — falling back to the standard addon flow for this page.");
    }
  }, 20000);
};

/**
 * Ensure Customily hook structure exists
 */
const ensureCustomilyHookStructure = () => {
  if (!window.customilyShopify.hooks) {
    window.customilyShopify.hooks = {};
  }
  if (!window.customilyShopify.hooks.cart) {
    window.customilyShopify.hooks.cart = {};
  }
};

/**
 * Get variant IDs selected via the catalog-style add-on picker
 * (assets/product-addon-catalog.js), read directly from the DOM since
 * those selections aren't checkboxes and don't feed `state.selectedAddons`.
 * @returns {number[]}
 */
const getCatalogAddonIds = () => {
  const ids = [];

  document.querySelectorAll(CONFIG.selectors.catalogItems).forEach((el) => {
    const variantId = parseInt(el.dataset.variantId);
    if (!isNaN(variantId) && !ids.includes(variantId)) {
      ids.push(variantId);
    }
  });

  return ids;
};

/**
 * Get addon items in Shopify format, merging checkbox-based addons and
 * catalog-picker addons into a single cart payload.
 * @returns {Array} Array of items with id and quantity
 */
const getAddonItems = () => {
  const checkboxIds = [...state.selectedAddons];
  const catalogIds = getCatalogAddonIds();
  const allIds = [...new Set([...checkboxIds, ...catalogIds])];

  return allIds.map((id) => ({ id, quantity: 1 }));
};

/**
 * Whether any addon (checkbox or catalog) is currently selected.
 * @returns {boolean}
 */
const hasAddonSelections = () => {
  return state.selectedAddons.size > 0 || getCatalogAddonIds().length > 0;
};

/**
 * The theme's cart UI component, if one exists on this page. Both
 * cart-drawer and cart-notification implement the same
 * renderContents(parsedState) / getSectionsToRender() API (assets/cart-drawer.js,
 * assets/cart-notification.js), matching what assets/product-form.js uses
 * for the main product.
 * @returns {Element|null}
 */
const getCartInstance = () => {
  return document.querySelector("cart-notification") || document.querySelector("cart-drawer");
};

/**
 * Add the currently selected addon items to the cart. When a cart-drawer or
 * cart-notification is present, requests its sections in the same request
 * (mirroring assets/product-form.js) and re-renders it with the result, so
 * the addon shows up without a page reload.
 * @returns {Promise<Object|null>} Cart response, or null if nothing to add
 */
const addAddonsToCart = async () => {
  const items = getAddonItems();
  if (items.length === 0) return null;

  const cart = getCartInstance();
  const payload = { items };

  if (cart) {
    payload.sections = cart.getSectionsToRender().map((section) => section.id);
    payload.sections_url = window.location.pathname;
  }

  const response = await addToCart(payload);

  if (cart) {
    cart.renderContents(response);
  }

  return response;
};

/**
 * Add items to cart via Shopify API
 * @param {FormData|Object} data - FormData or { items: [...] }
 * @returns {Promise<Object>} Cart response
 */
const addToCart = async (data) => {
  const isJSON = !(data instanceof FormData);

  const options = {
    method: "POST",
    body: isJSON ? JSON.stringify(data) : data,
  };

  if (isJSON) {
    options.headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  const response = await fetch(CONFIG.endpoints.cartAdd, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || errorData.description || "Failed to add to cart"
    );
  }

  return response.json();
};
